<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { get, patch } from '@/api'
import { useClientStore } from '@/stores/client'

definePage({
  meta: {
    title: '고객사 채널 설정',
    action: 'read',
    subject: 'DriverConfig',
  },
})

// 스토어
const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 드라이버 설정 인터페이스
interface DriverConfig {
  id: number
  download_driver: {
    id: number
    name: string
    description: string
    spec: {
      profileUrl: string
    }
  }
  driver_config: {
    profileUrl?: string
  }
}

interface FormattedDriverConfig {
  id: number
  channel: string
  profileUrl: string
  description: string
  originalProfileUrl: string
  profileGuide: string
}

// 상태
const driverConfigs = ref<DriverConfig[]>([])
const loading = ref(false)
const savingId = ref<number | null>(null)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', width: '80px' },
  { title: '채널', key: 'channel', width: '150px' },
  { title: '프로필 URL', key: 'profileUrl' },
  { title: '액션', key: 'actions', width: '100px', sortable: false },
]

// 포맷된 드라이버 설정
const formattedDriverConfigs = computed<FormattedDriverConfig[]>(() =>
  driverConfigs.value.map(config => ({
    id: config.id,
    channel: config.download_driver.name,
    profileUrl: config.driver_config.profileUrl ?? '',
    description: config.download_driver.description,
    originalProfileUrl: config.driver_config.profileUrl ?? '',
    profileGuide: config.download_driver.spec?.profileUrl ?? '',
  })),
)

// 드라이버 설정 로드
const loadDriverConfigs = async () => {
  if (!selectedClient.value?.id) {
    driverConfigs.value = []

    return
  }

  loading.value = true
  try {
    const { success, data } = await get<{ data: DriverConfig[] }>(
      `/review/clients/${selectedClient.value.id}/driver-configs`,
      { per_page: 100 },
    )

    if (success && data)
      driverConfigs.value = data.data || []
  }
  catch (error) {
    console.error('Failed to load driver configs:', error)
    showNotification('드라이버 설정을 불러오는데 실패했습니다.', 'error')
  }
  finally {
    loading.value = false
  }
}

// 프로필 URL 저장
const saveProfileUrl = async (configId: number, profileUrl: string) => {
  if (!selectedClient.value?.id)
    return

  const config = driverConfigs.value.find(c => c.id === configId)
  if (!config)
    return

  savingId.value = configId
  try {
    const { success } = await patch(
      `/review/clients/${selectedClient.value.id}/driver-configs/${configId}`,
      {
        driver_config: {
          ...config.driver_config,
          profileUrl,
        },
      },
    )

    if (success) {
      config.driver_config.profileUrl = profileUrl
      showNotification('프로필 URL이 저장되었습니다.')
    }
    else {
      showNotification('저장에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to save profile URL:', error)
    showNotification('저장에 실패했습니다.', 'error')
  }
  finally {
    savingId.value = null
  }
}

// 변경 여부 확인
const isChanged = (item: FormattedDriverConfig) => {
  return item.profileUrl !== item.originalProfileUrl
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 고객사 변경 감시
watch(selectedClient, () => {
  loadDriverConfigs()
}, { immediate: true })
</script>

<template>
  <VRow>
    <!-- 고객사 선택 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>고객사 채널 설정</VCardTitle>
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                :model-value="selectedClient?.id"
                :items="clientStore.clients"
                item-title="name"
                item-value="id"
                label="고객사 선택"
                placeholder="고객사를 선택하세요"
                clearable
                @update:model-value="(id: number | null) => clientStore.selectClientById(id)"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 드라이버 설정 목록 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>채널별 프로필 URL 설정</span>
          <VBtn
            icon="tabler-refresh"
            variant="text"
            :loading="loading"
            @click="loadDriverConfigs"
          />
        </VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headers"
            :items="formattedDriverConfigs"
            :loading="loading"
            density="compact"
            :items-per-page="50"
          >
            <template #item.profileUrl="{ item }">
              <div class="d-flex align-center gap-2">
                <VTextField
                  v-model="item.profileUrl"
                  density="compact"
                  hide-details
                  variant="outlined"
                  :placeholder="item.profileGuide"
                  :hint="item.description"
                  class="flex-grow-1"
                />
              </div>
              <small class="text-muted">{{ item.profileGuide }}</small>
            </template>

            <template #item.actions="{ item }">
              <VBtn
                size="small"
                color="primary"
                variant="outlined"
                :loading="savingId === item.id"
                :disabled="!isChanged(item)"
                @click="saveProfileUrl(item.id, item.profileUrl)"
              >
                저장
              </VBtn>
            </template>

            <template #no-data>
              <div class="text-center pa-4">
                {{ selectedClient ? '설정된 채널이 없습니다.' : '고객사를 선택해주세요.' }}
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VRow>
</template>
